import { action, computed, observable } from "mobx";

import {
	BrWidgetDefaultConfig,
	ExeFeedConfig,
	LsWidgetDefaultConfig,
} from "@features/live-widgets/shared";

import { IStoreLifeCycle } from "@interface";

import { LMTWidgetElement } from "@data-types/live-widget/LMTWidgetElement";
import { LMTConfigParams } from "@data-types/live-widget/LMTConfigParams";
import { WidgetInfo } from "@data-types/live-widget/WidgetInfo";
import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";

export class LiveMatchTrackerState implements IStoreLifeCycle {
	@observable public isStoreInitialized: boolean = false;

	@observable private lmtsDisabled = false;
	@observable private elements: LMTWidgetElement[] = [];

	/** @returns callback that can check if widget is loaded for selected container element and matchId. */
	@computed
	get activeWidgets() {
		return this.elements;
	}

	@computed public get findActiveWidget() {
		return (
			eventId: string,
			location: WidgetLocation
		): LMTWidgetElement | undefined => {
			return this.activeWidgets.find(
				(w) => w.location == location && w.eventId === eventId
			);
		};
	}

	@computed public get activeSideWidget() {
		return this.activeWidgets.find(
			(w) => w.location === WidgetLocation.SIDE
		);
	}

	@computed get isEmpty(): boolean {
		return this.elements.length === 0;
	}

	//#region constructor

	@action.bound
	public onInitialize(): void {
		this.isStoreInitialized = true;
	}

	//#endregion constructor

	//#region add, remove, update widget

	/** Toggle widget pin. */
	@action.bound
	toggleWidgetPin(eventId: string, location: WidgetLocation): void {
		const widget = this.activeWidgets.find(
			(w) => w.eventId === eventId && w.location === location
		);
		if (widget == null) {
			return;
		}

		this.updateItem({ ...widget, isPinned: !widget.isPinned });
	}

	/** Update item for given search object if it is in queue. */
	@action.bound
	public updateItem(newItem: LMTWidgetElement): void {
		const index = this.elements.findIndex((el) =>
			this.isWidgetActive(el, {
				eventId: newItem.eventId,
				location: newItem.location,
			})
		);
		if (index < 0 || index >= this.elements.length) {
			return;
		}

		this.elements[index] = newItem;
	}

	@action.bound
	public replaceWidgetMessage(newItem: LMTWidgetElement): void {}

	/** @param element remove from queue. */
	@action.bound
	remove(element: WidgetInfo): void {
		if (this.isEmpty) {
			return;
		}

		const elemIdx = this.elements.findIndex((el) =>
			this.isWidgetActive(el, element)
		);

		if (elemIdx > -1) {
			this.elements.splice(elemIdx, 1);
		}
	}

	/** Disable lmts and disposes all active widgets */
	@action.bound
	public toggleLMTWidget(config: LMTConfigParams): void {
		if (this.findActiveWidget(config.eventId, config.location)) {
			this.removeLMTWidget(config.eventId, config.location);
		} else {
			this.addLMTWidget(config);
		}
	}

	/** Add lmt widget, removes one from queue if it is at max length */
	@action.bound
	public addLMTWidget(config: LMTConfigParams): void {
		if (this.lmtsDisabled || !config.matchId) {
			if (config.location === WidgetLocation.SIDE) {
				this.removeLMTWidget("", WidgetLocation.SIDE);
			}
			return;
		}

		if (
			!this.isInQueue({
				eventId: config.eventId,
				location: config.location,
			})
		) {
			this.elements.push(this.createLMT(config));
		} else {
			this.updateItem(this.createLMT(config));
		}
	}

	createLMT(config: LMTConfigParams) {
		config.lsOptions = Object.assign({}, LsWidgetDefaultConfig);
		config.brOptions = Object.assign(
			{},
			BrWidgetDefaultConfig,
			config.brOptions
		);
		config.exeOptions = Object.assign({}, ExeFeedConfig);

		// see: https://widgets.sir.sportradar.com/docs/Widgets.html#.onTrack
		config.brOptions.onTrack = (event, data) => {
			if (event === "license_error") {
				console.error("LMT license error", data.error);
			}
		};
		return new LMTWidgetElement(config);
	}
	/** Removes widget from location for given eventId */
	@action.bound
	public removeLMTWidget(eventId: string, location: WidgetLocation): void {
		this.remove({ eventId, location });
	}

	//#endregion  add remove widget

	//#region helpers

	@action.bound
	private isWidgetActive(w: LMTWidgetElement, searchArgs: WidgetInfo) {
		return (
			(searchArgs.location === WidgetLocation.SIDE &&
				w.location === WidgetLocation.SIDE) ||
			(w.location == searchArgs.location &&
				w.eventId === searchArgs.eventId)
		);
	}

	/** @returns element is in queue based on passed predicate */
	public isInQueuePredicate(
		element: WidgetInfo,
		predicate: (a: LMTWidgetElement, b: WidgetInfo) => boolean
	): boolean {
		if (element == null || predicate == null) {
			return false;
		}

		return this.elements.findIndex((el) => predicate(el, element)) > -1;
	}

	/** @returns element is in queue based on filterPredicate given to constructor */
	public isInQueue(element: WidgetInfo): boolean {
		return this.isInQueuePredicate(element, this.isWidgetActive);
	}

	/** @returns true if sport radar widget is available */
	public isWidgetFunctionLoaded(): boolean {
		return window.SIR !== undefined;
	}

	//#endregion helpers

	//#region disposers

	/** Remove all active widgets */
	@action.bound
	public onDispose(): void {
		this.elements = [];
		this.isStoreInitialized = false;
	}

	//#endregion disposers
}
